/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import moment from 'moment';
import type Medication from 'linced-app/models/medical/medication';
import type School from 'linced-app/models/school';

// Proto: medical.Batch.
export default class Batch extends Model {
  declare [Type]: 'medical/batch';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the batch is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // medicationId is the id of the medication the batch relates to.
  @belongsTo('medical/medication', { async: true, inverse: 'batches' })
  declare medicationId: AsyncBelongsTo<Medication>;

  @alias('medicationId')
  declare medication: AsyncBelongsTo<Medication>;

  @alias('medication.id')
  declare medication_id: string | null | undefined;

  // number is the batch/sequence number of the batch
  @attr('string', { defaultValue: '' })
  declare number: string;

  // expiryDate is the expiry date of the batch in seconds from the epoch.
  @attr('number')
  declare expiryDate: number | undefined;

  // quantity is the quantity of the batch in stock.
  @attr('number', { defaultValue: 0 })
  declare quantity: number;

  // reviewDate is the review date of the batch in seconds from the epoch.
  @attr('number')
  declare reviewDate: number | undefined;

  // purchaseDate is the purchase date of the batch in seconds from the epoch.
  @attr('number')
  declare purchaseDate: number | undefined;

  // deleted is used to implement soft-deletes
  @attr('boolean', { defaultValue: false })
  declare deleted: boolean;

  @computed('expiryDate')
  get expiryDateString() {
    return this.expiryDate
      ? moment.unix(this.expiryDate).format('YYYY-MM-DD')
      : '';
  }

  @computed('reviewDate')
  get reviewDateString() {
    return this.reviewDate
      ? moment.unix(this.reviewDate).format('YYYY-MM-DD')
      : '';
  }

  @computed('purchaseDate')
  get purchaseDateString() {
    return this.purchaseDate
      ? moment.unix(this.purchaseDate).format('YYYY-MM-DD')
      : '';
  }

  @computed('number', 'expiryDate', 'quantity', 'reviewDate')
  get isValid() {
    if (isEmpty(this.number)) {
      return false;
    }
    if (isEmpty(this.quantity) || this.quantity < 0) {
      return false;
    }
    if (this.school.get('jurisdiction') === Jurisdiction.AU_NSW) {
      if (isEmpty(this.reviewDate)) {
        return false;
      }
      if (isEmpty(this.expiryDate)) {
        return false;
      }
    }

    return true;
  }

  @computed('number', 'expiryDate', 'quantity', 'reviewDate', 'purchaseDate')
  get hasBeenTouched() {
    if (!isEmpty(this.number)) {
      return true;
    }
    if (!isEmpty(this.expiryDate)) {
      return true;
    }
    // A quantity of 0 is the default, not a user change.
    if (this.quantity) {
      return true;
    }
    if (!isEmpty(this.reviewDate)) {
      return true;
    }
    if (!isEmpty(this.purchaseDate)) {
      return true;
    }
    return false;
  }
}
